import { BookmarkService, QueryParams } from '../../BookmarkService';
import { createRequestBuilder, HttpRequest, Logger } from '@quickplay/foundation';
import { AuthorizationUtil, PlatformAuthorizer } from '@quickplay/platform-authorizer';
import { BookmarkRecord, PlaylistBookmarkRecord } from '../modal/BookmarkRecord';
import { BookmarkCount, BookmarkResponse, GetBookmarkPlaylistResponse, SortBy, SortOrder } from './BookmarkResponse';
import { BookmarkRequest, Mode } from './BookmarkRequest';

const APPLICATION_JSON = 'application/json';

function mapPlaylistBookmarks(bookmarkData: BookmarkResponse): PlaylistBookmarkRecord[] | undefined {
  if (!bookmarkData.pb || bookmarkData.pb.length === 0) {
    return undefined;
  }

  return bookmarkData.pb.map((playlistBookmark: GetBookmarkPlaylistResponse) => ({
    playlistId: playlistBookmark.pid,
    videoId: playlistBookmark.vid,
    offset: playlistBookmark.o,
    timeStamp: playlistBookmark.ut,
  }));
}

export function createBookmarkServiceImpl(
  bookmarkEndPointUrl: string,
  platform: PlatformAuthorizer,
  logger?: Logger,
): BookmarkService {
  const bookmarkService: BookmarkService = {
    getBookmarks(options?: QueryParams): Promise<BookmarkRecord[]> {
      return fetchBookmarkCollection(`/user/bookmark/list`, options);
    },

    getBookmark(contentId: string): Promise<BookmarkRecord> {
      return getBookmark(contentId);
    },

    putBookmark(
      contentId: string,
      offset: number,
      seasonId?: string,
      episodeId?: string,
      mode?: Mode,
      videoId?: string,
      seasonNumber?: number,
      episodeNumber?: number,
      contentType?: string,
    ): Promise<void> {
      return putBookmark(
        contentId,
        offset,
        seasonId,
        episodeId,
        mode,
        videoId,
        seasonNumber,
        episodeNumber,
        contentType,
      );
    },

    deleteBookmark(contentId: string, mode?: Mode, episodeId?: string): Promise<void> {
      return deleteBookmark(contentId, mode, episodeId);
    },

    getBookmarkCount(): Promise<BookmarkCount> {
      return getBookmarkCount();
    },
    getSeriesBookmarks(seriesIdentifier: string, options?: QueryParams): Promise<BookmarkRecord[]> {
      return fetchBookmarkCollection(`/user/bookmark/series/${seriesIdentifier}/episodes`, options);
    },
    getPlaylistBookmarks(playlistId: string, options?: QueryParams): Promise<BookmarkRecord[]> {
      return fetchBookmarkCollection(`/user/bookmark/playlist/${playlistId}/videos`, options);
    },
  };

  async function getBookmark(contentId: string): Promise<BookmarkRecord> {
    try {
      const path = `/user/bookmark/lookup/${contentId}`;
      const result = await AuthorizationUtil.makeAuthorizedRequest<any, BookmarkResponse>(
        buildGetRequest(path),
        platform,
      );

      return {
        contentId: result.data.itemId,
        offset: result.data.offset,
        timeStamp: result.data.ut,
        seasonId: result.data.seasonId,
        episodeId: result.data.episodeId,
        seasonNumber: result.data.snum,
        episodeNumber: result.data.epnum,
        contentType: result.data.cty,
        videoId: result.data.videoId,
        mode: result.data.mode,
        playlistBookmarks: mapPlaylistBookmarks(result.data),
      };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function fetchBookmarkCollection(path: string, options?: QueryParams): Promise<BookmarkRecord[]> {
    const { pageNumber, pageSize, sortBy, sortOrder, id, includeWatchHistory } = options || {};

    const result = await AuthorizationUtil.makeAuthorizedRequest<any, BookmarkResponse>(
      buildGetRequest(path, pageNumber, pageSize, sortBy, sortOrder, id, includeWatchHistory),
      platform,
    );

    return getBookmarkRecords(result);
  }

  async function putBookmark(
    contentId: string,
    offset: number,
    seasonId?: string,
    episodeId?: string,
    mode?: Mode,
    videoId?: string,
    seasonNumber?: number,
    episodeNumber?: number,
    contentType?: string,
  ): Promise<void> {
    try {
      const request = buildPutBookmarkRequest(
        contentId,
        offset,
        seasonId,
        episodeId,
        mode,
        videoId,
        seasonNumber,
        episodeNumber,
        contentType,
      );
      await AuthorizationUtil.makeAuthorizedRequest<any, void>(request, platform);

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function deleteBookmark(contentId: string, mode?: Mode, episodeId?: string): Promise<void> {
    try {
      await AuthorizationUtil.makeAuthorizedRequest<any, void>(
        buildDeleteBookmarkRequest(contentId, mode, episodeId),
        platform,
      );
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function getBookmarkCount(): Promise<BookmarkCount> {
    try {
      const path = `/user/bookmark/count`;
      const result = await AuthorizationUtil.makeAuthorizedRequest<any, BookmarkCount>(buildGetRequest(path), platform);

      return result.data;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  function buildGetRequest(
    path: string,
    pageNumber?: number,
    pageSize?: number,
    sortBy?: SortBy,
    sortOrder?: SortOrder,
    id?: string,
    includeWatchHistory?: boolean,
  ): HttpRequest<any> {
    const queryParameters: any = {};
    if (pageNumber) queryParameters.pageNumber = pageNumber;
    if (pageSize) queryParameters.pageSize = pageSize;
    if (sortBy) queryParameters.sort = sortBy;
    if (sortOrder) queryParameters.order = sortOrder;
    if (id) queryParameters.id = id;
    if (includeWatchHistory) queryParameters.includeWatchHistory = includeWatchHistory;
    return createRequestBuilder()
      .url(bookmarkEndPointUrl)
      .path(path)
      .get()
      .queryParameters(queryParameters)
      .headers({ accept: APPLICATION_JSON })
      .build();
  }

  function buildPutBookmarkRequest(
    contentId: string,
    offset: number,
    seasonId?: string,
    episodeId?: string,
    mode?: Mode,
    videoId?: string,
    seasonNumber?: number,
    episodeNumber?: number,
    contentType?: string,
  ): HttpRequest<any> {
    const offsetValue = Math.floor(offset * 1000);
    const bookmarkRequest: BookmarkRequest = {
      itemId: contentId,
      offset: offsetValue,
      seasonId: seasonId,
      episodeId: episodeId,
      mode: mode,
      videoId: videoId,
      snum: seasonNumber,
      epnum: episodeNumber,
      cty: contentType,
    };
    return createRequestBuilder<BookmarkRequest>()
      .url(bookmarkEndPointUrl)
      .path(`/user/bookmark/create`)
      .put(bookmarkRequest)
      .headers({ accept: APPLICATION_JSON })
      .build();
  }

  function buildDeleteBookmarkRequest(contentId: string, mode?: Mode, episodeId?: string): HttpRequest<any> {
    const isValidEpisodeId = typeof episodeId === 'string' && episodeId.trim() !== '';
    let path = `/user/bookmark/delete/${contentId}`;

    if (mode === Mode.EPISODE) {
      if (isValidEpisodeId) {
        path = `/user/bookmark/series/${contentId}/episode/${episodeId}`;
      } else {
        logger?.warn(
          "Bookmark deletion requested in 'episode' mode without a valid episodeId. Please check integration.",
        );
      }
    } else if (mode === Mode.PLAYLIST) {
      if (isValidEpisodeId) {
        path = `/user/bookmark/playlist/${contentId}/video/${episodeId}`;
      } else {
        logger?.warn(
          "Bookmark deletion requested in 'playlist' mode without a valid videoId. Please check integration.",
        );
      }
    } else if (isValidEpisodeId) {
      logger?.warn(
        "Bookmark deletion includes episodeId or videoId, but playback mode is not set to 'episode' or 'playlist'. Please check integration.",
      );
    }

    return createRequestBuilder()
      .url(bookmarkEndPointUrl)
      .path(path)
      .delete('')
      .headers({ accept: APPLICATION_JSON })
      .build();
  }

  function getBookmarkRecords(result: any): BookmarkRecord[] {
    const bookmarkRecords: BookmarkRecord[] = [];
    result.data.forEach((bookmarkData: BookmarkResponse) => {
      const bookmarkRecord: BookmarkRecord = {
        contentId: bookmarkData.itemId,
        offset: bookmarkData.offset,
        timeStamp: bookmarkData.ut,
        seasonId: bookmarkData.seasonId,
        episodeId: bookmarkData.episodeId,
        seasonNumber: bookmarkData.snum,
        episodeNumber: bookmarkData.epnum,
        contentType: bookmarkData.cty,
        videoId: bookmarkData.videoId,
        mode: bookmarkData.mode,
        playlistBookmarks: mapPlaylistBookmarks(bookmarkData),
      };
      bookmarkRecords.push(bookmarkRecord);
    });
    return bookmarkRecords;
  }

  return bookmarkService;
}
